import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { Upload, X, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { usePhotoUploadWorkflow } from '@/hooks/usePhotos';
import type { PhotoParentType, PhotoFile } from '@/types/photos';

interface PhotoUploaderProps {
  parentType: PhotoParentType;
  parentId: string;
  maxPhotos?: number;
  maxFileSizeMB?: number;
  onUploadComplete?: (photoIds: string[], photoUrls: string[]) => void;
  onUploadError?: (error: Error) => void;
  className?: string;
  disabled?: boolean;
}

export function PhotoUploader({
  parentType,
  parentId,
  maxPhotos = 10,
  maxFileSizeMB = 10,
  onUploadComplete,
  onUploadError,
  className,
  disabled = false
}: PhotoUploaderProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [photoFiles, setPhotoFiles] = useState<PhotoFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadPhotos, isUploading } = usePhotoUploadWorkflow();

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files).filter(
      file => file.type.startsWith('image/')
    );

    handleFiles(files);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      handleFiles(files);
    }
  };

  const handleFiles = (files: File[]) => {
    const maxSizeBytes = maxFileSizeMB * 1024 * 1024;
    const validFiles = files.filter(file => {
      if (file.size > maxSizeBytes) {
        console.warn(`File ${file.name} exceeds ${maxFileSizeMB}MB limit`);
        return false;
      }
      return true;
    });

    const remainingSlots = maxPhotos - selectedFiles.length;
    const filesToAdd = validFiles.slice(0, remainingSlots);

    setSelectedFiles(prev => [...prev, ...filesToAdd]);
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0 || !parentId) return;

    try {
      const uploadedPhotos = await uploadPhotos(
        parentType,
        parentId,
        selectedFiles,
        (files) => setPhotoFiles(files)
      );

      const photoIds = uploadedPhotos.map(p => p.id);
      const photoUrls = uploadedPhotos.map(p => p.url || '');
      onUploadComplete?.(photoIds, photoUrls);

      // Clear selected files after successful upload
      setSelectedFiles([]);
      setPhotoFiles([]);

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      onUploadError?.(error as Error);
    }
  };

  const getFileStatus = (file: File): PhotoFile | undefined => {
    return photoFiles.find(pf => pf.file === file);
  };

  const renderFileStatus = (file: File, index: number) => {
    const status = getFileStatus(file);

    if (!status || status.status === 'pending') {
      return (
        <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="flex-shrink-0 w-10 h-10 rounded overflow-hidden bg-background">
              <img
                src={URL.createObjectURL(file)}
                alt={file.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{file.name}</p>
              <p className="text-xs text-muted-foreground">
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
          </div>
          {!isUploading && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleRemoveFile(index)}
              className="flex-shrink-0"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      );
    }

    return (
      <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="flex-shrink-0">
            {status.status === 'uploading' && <Loader2 className="h-5 w-5 animate-spin text-primary" />}
            {status.status === 'uploaded' && <Loader2 className="h-5 w-5 animate-spin text-primary" />}
            {status.status === 'confirmed' && <CheckCircle2 className="h-5 w-5 text-green-600" />}
            {status.status === 'error' && <AlertCircle className="h-5 w-5 text-destructive" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{file.name}</p>
            {status.status === 'error' && status.error && (
              <p className="text-xs text-destructive">{status.error}</p>
            )}
            {status.status === 'uploading' && (
              <Progress value={status.progress || 0} className="h-1 mt-1" />
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={cn('space-y-4', className)}>
      <div
        className={cn(
          'border-2 border-dashed rounded-lg p-8 text-center transition-colors',
          isDragging && !disabled ? 'border-primary bg-primary/10' : 'border-border',
          disabled && 'opacity-50 cursor-not-allowed',
          !disabled && 'cursor-pointer hover:border-primary/50'
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !disabled && fileInputRef.current?.click()}
      >
        <Upload className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
        <p className="text-sm font-medium mb-1">
          Drag and drop photos here, or click to browse
        </p>
        <p className="text-xs text-muted-foreground">
          Max {maxPhotos} photos, up to {maxFileSizeMB}MB each
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFileInput}
          disabled={disabled}
        />
      </div>

      {selectedFiles.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">
              {selectedFiles.length} photo{selectedFiles.length !== 1 ? 's' : ''} selected
            </p>
            {!isUploading && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedFiles([])}
              >
                Clear all
              </Button>
            )}
          </div>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {selectedFiles.map((file, index) => (
              <div key={`${file.name}-${index}`}>
                {renderFileStatus(file, index)}
              </div>
            ))}
          </div>

          {!isUploading && (
            <Button
              onClick={handleUpload}
              disabled={disabled || selectedFiles.length === 0}
              className="w-full"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload {selectedFiles.length} photo{selectedFiles.length !== 1 ? 's' : ''}
            </Button>
          )}

          {isUploading && (
            <div className="text-center py-2">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-primary" />
              <p className="text-sm text-muted-foreground">Uploading photos...</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
